import ElakayImage from '../../../components/images/ElakayImage';
import FollowShopButton from "../../shops/FollowShopButton";
// The Provider Shop website. Same architecture and look as the Seller shop (/lakay-ShopSeller/:id):
//
//   shop header (shared HeaderShopSeller: name, Share, Search, ☰ / Refresh / Elakay / Home / About / Filter)
//     + the Provider's own menu pages + PROVIDER MANAGEMENT (owner only: Dashboard | Dropshipping In | My Products)
//   identity (logo, name, real statistics, real rating, Like Provider)
//   hero      the Provider's Home page from the Provider Page Builder, else a clean default hero
//   Shop Categories   the Provider's real categories (published products only)
//   Products          server-paginated B2B cards (search / category / Filters Shop / sort)
//   Footer            the common Elakay footer (Layout)
//
// Only PROVIDER data is ever read here (storefront API + Provider catalogue API, both scoped by
// providerId); the Seller shop API is never called. `mode`: "public" (Sellers / professional roles on
// /provider-shop/:id) or "owner" (the Provider's own Customer View: same website, Seller-only actions
// inert). Every piece of navigation is a router link (no full reload); shop state (search, category,
// filters, product page) lives in the URL so it is shareable and survives a refresh.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ShopHeroSlider, { useShopHeroConfig } from "../../dynamicSwipers/ShopHeroSlider";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FaHeart, FaRegHeart, FaStar } from "react-icons/fa";
import HeaderShopSeller from "../../Layout/headerShopSeller";
import PageRenderer from "../../pageInside/PageBuilder/PageRenderer";
import { elakayAutoText as text, elakayPluralText } from "../../pageInside/dataElakay";
import { toImageUrl } from "../../../config";
import { getProductGridLimit } from "../../../utils/paginationSettings";
import { setDrawerFilters, clearDrawerFilters, openFilterDrawer } from "../../../components/SIdeDrawer/drawerFilterSlot";
import { buildShopManagementLinks, isShopOwner } from "../../Layout/shopManagementNavigation";
import { FILTER_CONTEXTS } from "../../../components/SIdeDrawer/filterContext";
import { listProviderCatalogForSeller, likeProvider, unlikeProvider } from "../services/providerProductService";
import ProviderFiltersPanel from "../filters/ProviderFiltersPanel";
import { countActiveFilters, filtersFromParams, filtersToParams } from "../filters/providerFilterState";
import { GridSkeleton, useCardMode } from "../ui/ProductGrid";
import ProductPagination from "../ui/ProductPagination";
import { buildStorefrontPageData } from "../center/storefrontData";
import { providerStorefrontPath, providerStorefrontAboutPath } from "../providerWorkspace";
import ProviderStorefrontView from "./ProviderStorefrontView";
import { buildProviderStoreConfig, ProviderStats, StorefrontProductCard } from "./providerStoreConfig";
import { makeProviderAboutDoc } from "./providerAboutDoc";
import { navigationPages, pagesOf } from "./storefrontPages";
import "../ui/ui.css";
import "./providerStorefront.css";
import "./providerShop.css";
import { providerOwnerName, providerShopDisplayName, providerShopMonogram } from "../utils/providerShopIdentity";

const SORTS = [["new", "h_pc_s_new", "Newest"], ["popular", "h_pc_s_pop", "Best sellers"], ["price_asc", "h_pc_s_pa", "Price: low to high"], ["price_desc", "h_pc_s_pd", "Price: high to low"], ["name", "h_pc_s_name", "Name (A-Z)"]];
const catLabel = (c) => (Array.isArray(c?.name) ? c.name[0] : String(c?.name || ""));
const ok = (r) => r && !r.error && r.success !== false;

// ── Adapter: storefront API response -> the shop the UI renders (Provider data only) ─────────────
export const buildProviderShopAdapter = (summary) => {
  const id = summary?.provider?._id ? String(summary.provider._id) : "";
  const stats = summary?.stats || {};
  const custom = summary?.custom || null;
  return {
    shopType: "provider",
    id,
    // Multi-shop: the Provider shop shown (its products / facets only). The original shop has an id
    // too once materialized; absent = legacy data only.
    shopId: summary?.currentShop?._id ? String(summary.currentShop._id) : null,
    // Public shop identity (shop name, else owner initials) -- the owner's full name is kept apart.
    name: providerShopDisplayName(summary?.shop, summary?.provider) || text("h_psh_provider", "Provider"),
    monogram: providerShopMonogram(summary?.shop, summary?.provider),
    ownerName: providerOwnerName(summary?.provider),
    logo: summary?.shop?.avatar ? toImageUrl(summary.shop.avatar) : "",
    location: summary?.shop?.location || "",
    roleName: summary?.provider?.roleName || "Provider",
    stats,
    rating: Number.isFinite(stats.rating) ? { value: stats.rating, count: stats.ratingCount || 0 } : null,
    likes: Number.isFinite(stats.likes) ? stats.likes : null,
    capabilities: summary?.capabilities || null,
    viewer: summary?.viewer || { isOwner: false, isLiked: false },
    settings: summary?.settings || null,
    custom,
    homePageId: custom?.homePageId || null,
    aboutPageId: custom?.aboutPageId || null,
    pages: custom?.pages || [],
  };
};

// Links of the shop website. Public: /provider-shop/:id[/about][?page=]. Customer View: the Provider's
// /provider/my-shop?view=customer[&section=about][&page=] -- the same website on the owner route.
export const publicShopPaths = (providerId, shopId = null) => ({
  home: providerStorefrontPath(providerId, null, shopId),
  about: providerStorefrontAboutPath(providerId, shopId),
  page: (pageId) => providerStorefrontPath(providerId, pageId, shopId),
});

export const withQuery = (path, patch = {}) => {
  const [base, search = ""] = String(path).split("?");
  const next = new URLSearchParams(search);
  Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
  const qs = next.toString();
  return qs ? `${base}?${qs}` : base;
};

// Shop query in the URL: q (search), p (product page), sort, and the Filters Shop criteria (category
// included). `page` is the storefront page id and is never touched here.
export const useShopQuery = () => {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const q = params.get("q") || "";
  const sort = params.get("sort") || "new";
  const productPage = Math.max(1, parseInt(params.get("p"), 10) || 1);
  const update = useCallback((patch, { keepPage = false } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!keepPage) next.delete("p");
    setParams(next, { replace: true });
  }, [params, setParams]);
  const setFilters = useCallback((nextFilters) => {
    const next = filtersToParams(nextFilters, params);
    const pageId = params.get("page");
    if (pageId) next.set("page", pageId);
    next.delete("p");
    setParams(next, { replace: true });
  }, [params, setParams]);
  const clear = useCallback(() => {
    const next = filtersToParams({}, params);
    ["q", "p", "sort"].forEach((k) => next.delete(k));
    const pageId = params.get("page");
    if (pageId) next.set("page", pageId);
    setParams(next, { replace: true });
  }, [params, setParams]);
  return { q, sort, productPage, filters, update, setFilters, clear };
};

// Provider management shortcuts (Dashboard | Dropshipping In | My Products): the shared shop header
// builds them from the central shop management config (Layout/shopManagementNavigation.js) for the
// shop's owner only. Kept as a named export for existing callers.
export const providerManagementLinks = (infoSource, canManage = true) =>
  buildShopManagementLinks({ shopType: "provider", infoSource, canManage });

const scrollToProducts = () => {
  if (typeof document === "undefined") return;
  document.getElementById("psh-products")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
};

// ── HEADER ───────────────────────────────────────────────────────────────────────────────────────
// The shared shop header (the Seller shop's HeaderShopSeller) with Provider links. `inline`: rendered
// inside a page (Customer View) instead of as the Layout header.
export function ProviderShopHeader({ infoSource, shop, paths, section = "home", activePageId = null, drawerClickHander, onRefresh, inline = false }) {
  const navigate = useNavigate();
  const { q, update } = useShopQuery();
  const [search, setSearch] = useState(q);
  const typed = useRef(false);
  useEffect(() => { if (!typed.current) setSearch(q); }, [q]);

  // Debounced "search this shop": results always come from this Provider's products only.
  useEffect(() => {
    if (!typed.current) return undefined;
    const t = setTimeout(() => {
      typed.current = false;
      if (search === q && section === "home") return;
      if (section === "home") update({ q: search });
      else navigate(withQuery(paths.home, { q: search }));
      setTimeout(scrollToProducts, 60);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const onFilter = () => {
    if (section !== "home") navigate(paths.home);
    openFilterDrawer();
  };
  const homeActive = section === "home" && (!activePageId || !shop.homePageId || String(activePageId) === String(shop.homePageId));
  const menuPages = navigationPages(shop.pages).filter((r) => String(r.id) !== String(shop.homePageId) && String(r.id) !== String(shop.aboutPageId));

  // PROVIDER MANAGEMENT group + ☰ drawer navigation: rendered by the shared header (shopType
  // "provider"), for the shop's owner only (server-confirmed, or the owner's own Customer View).
  const canManageShop = isShopOwner(infoSource, [shop.id], Boolean(shop.viewer?.isOwner) || inline);
  const pagesMenu = menuPages.map((r) => (
    <li className="headershop-menu-item headershop-menu-item--page psh-menu-item--page" key={r.id}>
      <Link to={paths.page(r.id)} className="headershop-menu-link" aria-current={section === "home" && String(activePageId) === String(r.id) ? "page" : undefined}>{r.name}</Link>
    </li>
  ));
  const drawerPages = useMemo(() => menuPages.map((r) => ({ key: `page-${r.id}`, label: r.name, to: paths.page(r.id) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(menuPages.map((r) => [r.id, r.name])), paths]);

  return (
    <div className={`psh-header${inline ? " psh-header--inline" : ""}`} data-shop-type="provider">
      <HeaderShopSeller
        infoSource={infoSource}
        userID={shop.id}
        shopName={shop.name}
        titleText={shop.name}
        shopPath={paths.home}
        aboutPath={paths.about}
        shareUrl={shop.id && typeof window !== "undefined" ? `${window.location.origin}${providerStorefrontPath(shop.id)}` : undefined}
        searchPlaceholder={text("h_psh_search", "Search this shop")}
        search={search}
        loadindSearCh={(value) => { typed.current = true; setSearch(value); }}
        onFilterShop={onFilter}
        onRefresh={onRefresh}
        drawerClickHander={drawerClickHander}
        outOffPage={false}
        showFilter={true}
        activeSection={section === "about" ? "about" : homeActive ? "home" : null}
        menuEndItems={pagesMenu}
        shopType="provider"
        canManageShop={canManageShop}
        drawerTitle={shop.name}
        drawerShopItems={drawerPages}
      />
    </div>
  );
}

// ── IDENTITY: logo, name, real statistics / rating, Like Provider ────────────────────────────────
export function ProviderShopIdentity({ shop, mode = "public", token = null }) {
  const [liked, setLiked] = useState(Boolean(shop.viewer?.isLiked));
  const [likes, setLikes] = useState(shop.likes);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setLiked(Boolean(shop.viewer?.isLiked)); setLikes(shop.likes); }, [shop.viewer?.isLiked, shop.likes]);
  const canLike = mode === "public" && !shop.viewer?.isOwner && Boolean(token) && Boolean(shop.id);

  // The like state only changes once the server confirmed it (never a local-only toggle).
  const toggleLike = async () => {
    setBusy(true);
    setError("");
    const r = liked ? await unlikeProvider(token, shop.id) : await likeProvider(token, shop.id);
    setBusy(false);
    if (!ok(r)) { setError(r?.error || text("h_spm_error_title", "Something went wrong")); return; }
    const next = Boolean(r.liked);
    if (next !== liked && Number.isFinite(likes)) setLikes(Math.max(0, likes + (next ? 1 : -1)));
    setLiked(next);
  };
  const stats = { ...shop.stats, likes };

  return (
    <section className="psh-identity" aria-label={shop.name}>
      {shop.logo
        ? <ElakayImage preset="avatar-tiny" className="psh-identity__logo" src={shop.logo} alt="" />
        : <span className="psh-identity__logo psh-identity__logo--ph" aria-hidden="true">{shop.monogram}</span>}
      <div className="psh-identity__text">
        <h1>{shop.name}</h1>
        <p>
          <span className="psh-badge">{shop.roleName === "Producer" ? text("h_spm_producer", "Producer") : text("h_spm_provider", "Provider")}</span>
          {shop.location && <span className="psh-identity__location">{shop.location}</span>}
          {!shop.rating && <span className="psh-identity__noreviews"><FaStar aria-hidden="true" /> {text("h_psh_no_reviews", "No reviews yet")}</span>}
        </p>
      </div>
      <ProviderStats stats={stats} />
      {canLike && (
        <button type="button" className={`psh-like${liked ? " psh-like--on" : ""}`} aria-pressed={liked} disabled={busy} onClick={toggleLike}>
          {liked ? <FaHeart aria-hidden="true" /> : <FaRegHeart aria-hidden="true" />}
          <span>{liked ? text("h_psh_liked", "Liked") : text("h_psh_like", "Like Provider")}</span>
        </button>
      )}
      {mode !== 'owner' && !shop.viewer?.isOwner && <FollowShopButton ownerId={shop.id} shopId={shop.shopId} token={token} />}
      {error && <p className="psh-identity__error" role="alert">{error}</p>}
    </section>
  );
}

// ── DEFAULT HERO (no Home page built with the Provider Page Builder) ─────────────────────────────
export function ProviderShopHero({ shop, paths }) {
  const services = [
    shop.capabilities?.wholesale && text("h_psh_hero_ws", "Wholesale"),
    shop.capabilities?.dropshipping && text("h_psh_hero_ds", "Dropshipping"),
    Number.isFinite(shop.stats?.activeProducts) && shop.stats.activeProducts > 0 && elakayPluralText(shop.stats.activeProducts, ["h_pc_count_one", "{value0} product"], ["h_psh_hero_products", "{value0} products"], { value0: shop.stats.activeProducts }),
  ].filter(Boolean);
  return (
    <section className="psh-hero" aria-labelledby="psh-hero-title">
      <div className="psh-hero__inner">
        <p className="psh-hero__eyebrow">{shop.roleName === "Producer" ? text("h_psh_hero_eyebrow_p", "Producer on Elakay") : text("h_psh_hero_eyebrow", "Provider on Elakay")}</p>
        <h2 id="psh-hero-title">{text("h_psh_hero_title", "Welcome to {value0}", { value0: shop.name })}</h2>
        <p className="psh-hero__lead">{text("h_psh_hero_lead", "Source products for your shop: buy in volume or sell them in your Elakay shop.")}</p>
        {services.length > 0 && <ul className="psh-hero__chips">{services.map((s) => <li key={s}>{s}</li>)}</ul>}
        <div className="psh-hero__cta">
          <button type="button" className="psh-btn psh-btn--light" onClick={scrollToProducts}>{text("h_psh_hero_browse", "Browse products")}</button>
          <Link className="psh-btn psh-btn--ghost" to={paths.about}>{text("h_psh_hero_about", "About us")}</Link>
        </div>
      </div>
    </section>
  );
}

// ── SHOP CATEGORIES (real categories of published products; hidden when there are none) ─────────
export function ProviderShopCategories({ categories = [], previewProducts = [], activeId = "", onSelect, total = null }) {
  if (!categories.length) return null;
  const imagesOf = (categoryId) => previewProducts
    .filter((p) => [p.categoryId, ...(p.categoryIds || [])].filter(Boolean).map(String).includes(String(categoryId)))
    .map((p) => p.images?.[0]).filter(Boolean).slice(0, 4).map((src) => toImageUrl(src));
  const card = (key, label, count, images, active, onClick) => (
    <li key={key}>
      <button type="button" className={`psh-cat${active ? " psh-cat--active" : ""}`} aria-pressed={active} onClick={onClick}>
        <span className="psh-cat__mosaic" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (images[i] ? <ElakayImage preset="product-card" key={i} src={images[i]} alt="" loading="lazy" /> : <span key={i} className="psh-cat__ph" />))}
        </span>
        <span className="psh-cat__name">{label}</span>
        {Number.isFinite(count) && <span className="psh-cat__count">{elakayPluralText(count, ["h_pc_count_one", "{value0} product"], ["h_pc_count", "{value0} products"], { value0: count })}</span>}
      </button>
    </li>
  );
  return (
    <section className="psh-section" aria-labelledby="psh-cats-title">
      <h2 id="psh-cats-title" className="psh-section__title">{text("h_psh_categories", "Shop Categories")}</h2>
      <ul className="psh-cats">
        {card("all", text("h_psh_all_products", "All Products"), total, previewProducts.map((p) => p.images?.[0]).filter(Boolean).slice(0, 4).map((src) => toImageUrl(src)), !activeId, () => onSelect(""))}
        {categories.map((c) => card(c._id, catLabel(c), c.products, imagesOf(c._id), String(activeId) === String(c._id), () => onSelect(String(c._id))))}
      </ul>
    </section>
  );
}

// ── PRODUCTS (server pagination, search, category, Filters Shop, sort) ───────────────────────────
export function ProviderShopProducts({ shop, categories = [], mode = "public", token = null, infoSource, refreshKey = 0 }) {
  const { q, sort, productPage, filters, update, setFilters, clear } = useShopQuery();
  const limit = getProductGridLimit(infoSource?.allSettings, "productGridShop", 12);
  const cardMode = useCardMode();
  const [state, setState] = useState({ loading: true, error: "", items: [], total: 0 });
  const [retry, setRetry] = useState(0);
  const seq = useRef(0);
  const fkey = JSON.stringify(filters);

  useEffect(() => {
    if (!shop.id) return undefined;
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    listProviderCatalogForSeller(token, { providerId: shop.id, shopId: shop.shopId, page: productPage, limit, q, sort, filters }).then((r) => {
      if (mine !== seq.current) return; // a newer query answered first -- never show stale results
      if (!ok(r)) setState({ loading: false, error: r?.error || text("h_spm_error_title", "Something went wrong"), items: [], total: 0 });
      else setState({ loading: false, error: "", items: r.items || [], total: Number(r.total) || 0 });
    });
    return () => { seq.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop.id, shop.shopId, token, productPage, limit, q, sort, fkey, refreshKey, retry]);

  // Filters Shop in the Elakay Filter drawer: this Provider's own facets only.
  const setFiltersRef = useRef(setFilters);
  setFiltersRef.current = setFilters;
  useEffect(() => {
    if (!shop.id) return undefined;
    setDrawerFilters({ owner: "providerShop", context: FILTER_CONTEXTS.PROVIDER_SHOP, render: ({ click, gridButon, embedded }) => (
      <ProviderFiltersPanel mode="shop" filters={filters} scope={{ providerId: shop.id, ...(shop.shopId && { shopId: shop.shopId }) }} onChange={(n) => setFiltersRef.current(n)} embedded={embedded} click={click} gridButon={gridButon} infoSource={infoSource} />) });
    return () => clearDrawerFilters("providerShop");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fkey, shop.id]);

  const active = countActiveFilters(filters);
  const category = categories.find((c) => String(c._id) === String(filters.categoryId));
  const title = q
    ? text("h_psh_results_for", "Results for “{value0}”", { value0: q })
    : category ? catLabel(category) : text("h_psh_all_products", "All Products");
  const narrowed = Boolean(q) || active > 0;

  return (
    <section id="psh-products" className="psh-section psh-products" aria-labelledby="psh-products-title">
      <div className="psh-products__head">
        <h2 id="psh-products-title" className="psh-section__title">{title}</h2>
        <div className="psh-products__tools">
          <span className="psh-products__count" role="status">{state.loading ? "" : elakayPluralText(state.total, ["h_pc_count_one", "{value0} product"], ["h_pc_count", "{value0} products"], { value0: state.total })}</span>
          <button type="button" className="psh-btn psh-btn--outline" onClick={openFilterDrawer}>{text("h_spm_filters", "Filters")}{active ? ` (${active})` : ""}</button>
          <select aria-label={text("h_spm_sort", "Sort by")} value={sort} onChange={(e) => update({ sort: e.target.value === "new" ? "" : e.target.value })}>
            {SORTS.map(([v, k, l]) => <option key={v} value={v}>{text(k, l)}</option>)}
          </select>
          {narrowed && <button type="button" className="psh-btn psh-btn--link" onClick={clear}>{text("h_pff_clear", "Clear All")}</button>}
        </div>
      </div>
      {state.error ? (
        <div className="dsi-state-card" role="alert">
          <p>{state.error}</p>
          <button type="button" className="dsi-state-card__btn" onClick={() => setRetry((n) => n + 1)}>{text("h_psh_retry", "Try again")}</button>
        </div>
      ) : state.loading && !state.items.length ? (
        <GridSkeleton count={Math.min(limit, 8)} />
      ) : !state.items.length ? (
        <p className="psf-empty">{narrowed
          ? text("h_pc_noresult", "No products match your criteria.")
          : text("h_psh_no_products", "This provider has no published products yet.")}</p>
      ) : (
        <div className={`pgrid psf-grid${cardMode === "landscape" ? " pgrid--landscape" : ""}`} aria-busy={state.loading}>
          {state.items.map((p) => <StorefrontProductCard key={p._id} product={p} mode={mode} capabilities={shop.capabilities} />)}
        </div>
      )}
      {!state.error && (
        <ProductPagination page={productPage} limit={limit} total={state.total} infoSource={infoSource}
          onPage={(n) => { update({ p: n > 1 ? String(n) : "" }, { keepPage: true }); setTimeout(scrollToProducts, 30); }} />
      )}
    </section>
  );
}

// ── HOME (identity + Page Builder page or default hero + categories + products) ──────────────────
export function ProviderShopHome({ summary, custom, previewProducts = [], categories = [], pageId = null, paths, mode = "public", infoSource, token = null, refreshKey = 0, productsLoading = false }) {
  const shop = useMemo(() => buildProviderShopAdapter(summary), [summary]);
  // Main Shop Slider Source: an Elakay Swiper chosen by the Provider replaces the default hero.
  const heroConfig = useShopHeroConfig(shop.id || null, shop.shopId);
  const { filters, setFilters } = useShopQuery();
  const selectCategory = (id) => { setFilters({ ...filters, categoryId: id, attr: [] }); setTimeout(scrollToProducts, 60); };
  const navigate = useNavigate();
  return (
    <ProviderStorefrontView
      summary={summary}
      custom={custom}
      products={previewProducts}
      categories={categories}
      pageId={pageId}
      onSelectPage={(id) => navigate(paths.page(id))}
      mode={mode}
      infoSource={infoSource}
      token={token}
      productsLoading={productsLoading}
      showNav={false}
      renderHeader={() => <ProviderShopIdentity shop={shop} mode={mode} token={token} />}
      fallbackHero={<ShopHeroSlider hero={heroConfig} ownerId={shop.id || null}><ProviderShopHero shop={shop} paths={paths} /></ShopHeroSlider>}
      renderCatalog={() => (
        <div className="psh-catalog">
          <ProviderShopCategories categories={categories} previewProducts={previewProducts} activeId={filters.categoryId} onSelect={selectCategory} total={shop.stats?.activeProducts} />
          <ProviderShopProducts shop={shop} categories={categories} mode={mode} token={token} infoSource={infoSource} refreshKey={refreshKey} />
        </div>
      )}
    />
  );
}

const deviceFor = (width) => (width <= 480 ? "mobile" : width <= 768 ? "tablet" : "desktop");

// ── ABOUT (the Provider's chosen About page, else the generated Provider About page) ─────────────
export function ProviderShopAbout({ summary, custom, previewProducts = [], categories = [], paths, mode = "public", infoSource, token = null }) {
  const navigate = useNavigate();
  const shop = useMemo(() => buildProviderShopAdapter(summary), [summary]);
  const storeConfig = useMemo(() => buildProviderStoreConfig({ mode, navigate }), [mode, navigate]);
  const pageData = useMemo(() => buildStorefrontPageData(summary, previewProducts, categories), [summary, previewProducts, categories]);
  const generated = useMemo(() => makeProviderAboutDoc({ name: shop.name, roleName: shop.roleName }), [shop.name, shop.roleName]);
  const [width, setWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1200));
  useEffect(() => {
    const on = () => setWidth(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const aboutId = custom?.aboutPageId;
  const hasCustomAbout = Boolean(aboutId && pagesOf(custom?.doc).some((p) => String(p.id) === String(aboutId)));

  return (
    <div className="psh-about" data-about-source={hasCustomAbout ? "provider-page" : "generated"}>
      {hasCustomAbout ? (
        <ProviderStorefrontView summary={summary} custom={custom} products={previewProducts} categories={categories} pageId={aboutId}
          onSelectPage={(id) => navigate(paths.page(id))} mode={mode} infoSource={infoSource} token={token}
          showNav={false} renderHeader={() => null} renderCatalog={() => null} />
      ) : (
        <div className="psf">
          <PageRenderer doc={generated} pageData={pageData} infoSource={infoSource} token={token} device={deviceFor(width)} storeConfig={storeConfig} />
        </div>
      )}
      <div className="psh-about__cta">
        <Link className="psh-btn psh-btn--primary" to={paths.home}>{text("h_psh_visit_shop", "Visit our shop")}</Link>
      </div>
    </div>
  );
}
